import { Link } from 'react-router-dom';
import HeroSection from './components/HeroSection';
import FeaturedGrid from './components/FeaturedGrid';
import CTASection from './components/CTASection';
import { comics, categories, newComics, hotComics } from '@/mocks/comics';

function LatestUpdates() {
  const latestList = newComics.map((id) => comics.find((c) => c.id === id)!);
  const hotList = hotComics.map((id) => comics.find((c) => c.id === id)!);

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                最新更新
              </h3>
              <Link
                to="/category"
                className="text-primary-500 hover:text-primary-600 text-sm font-medium transition-colors duration-200 whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-s-line ml-1"></i>
              </Link>
            </div>
            <div className="flex flex-col gap-3">
              {latestList.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="flex items-center gap-4 p-3 rounded-xl bg-background-50 hover:bg-background-200/50 transition-all duration-200 group cursor-pointer"
                >
                  <div className="w-16 h-20 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-foreground-950 font-medium text-sm group-hover:text-primary-600 transition-colors duration-200 truncate">
                      {comic.title}
                    </h4>
                    <p className="text-foreground-400 text-xs mt-1">
                      {comic.author} · {comic.categoryName}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-accent-500 text-xs font-medium">
                      {comic.rating}
                    </span>
                    <p className="text-foreground-400 text-[10px] mt-0.5">
                      {comic.updatedAt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                热门排行
              </h3>
              <Link
                to="/category?tab=hot"
                className="text-primary-500 hover:text-primary-600 text-sm font-medium transition-colors duration-200 whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-s-line ml-1"></i>
              </Link>
            </div>
            <div className="flex flex-col gap-3">
              {hotList.map((comic, index) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="flex items-center gap-4 p-3 rounded-xl bg-background-50 hover:bg-background-200/50 transition-all duration-200 group cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <span className={`text-sm font-bold ${
                      index < 3 ? 'text-accent-600' : 'text-foreground-400'
                    }`}>
                      {index + 1}
                    </span>
                  </div>
                  <div className="w-16 h-20 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-foreground-950 font-medium text-sm group-hover:text-primary-600 transition-colors duration-200 truncate">
                      {comic.title}
                    </h4>
                    <p className="text-foreground-400 text-xs mt-1">
                      {comic.author}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-foreground-600 text-xs">
                      {comic.views > 10000
                        ? `${(comic.views / 10000).toFixed(0)}万`
                        : comic.views}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CategoryQuickLinks() {
  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <p className="text-accent-500 text-sm font-medium tracking-widest mb-3">
            分类导航
          </p>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            探索韩漫分类
          </h2>
          <p className="text-foreground-500 text-sm md:text-base mt-3 max-w-lg mx-auto">
            六大分类任您选择，找到最对味的韩国漫画
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              className="group flex flex-col items-center gap-3 p-5 md:p-6 rounded-xl bg-background-100 hover:bg-primary-50 transition-all duration-300 cursor-pointer"
            >
              <span className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-accent-100 flex items-center justify-center group-hover:bg-accent-200 transition-colors duration-300">
                <i className={`text-accent-600 text-xl md:text-2xl ${
                  cat.slug === 'romance' ? 'ri-heart-line' :
                  cat.slug === 'action' ? 'ri-sword-line' :
                  cat.slug === 'thriller' ? 'ri-search-eye-line' :
                  cat.slug === 'fantasy' ? 'ri-magic-line' :
                  cat.slug === 'urban' ? 'ri-building-line' :
                  'ri-ancient-gate-line'
                }`}></i>
              </span>
              <span className="text-foreground-800 font-medium text-sm group-hover:text-primary-600 transition-colors duration-300 whitespace-nowrap">
                {cat.name}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const faqs = [
    {
      q: '韩漫画网的漫画可以免费阅读吗？',
      a: '是的，韩漫画网提供下拉式完整版韩漫免费阅读服务，所有漫画内容均可免费在线阅读，无需付费。',
    },
    {
      q: '韩漫画网的漫画是完整版还是删减版？',
      a: '韩漫画网坚持提供无删减漫画免费阅读，所有内容均为完整版，确保您获得最原汁原味的阅读体验。',
    },
    {
      q: '韩漫画网的漫画更新频率如何？',
      a: '韩漫大全每日更新，我们拥有专业的更新团队，确保热门连载漫画保持同步更新，让您总有新书可看。',
    },
    {
      q: '韩漫画网支持哪些设备阅读？',
      a: '韩漫画网采用下拉式阅读模式，完美适配电脑、平板和手机等各种设备，随时随地享受韩漫阅读乐趣。',
    },
    {
      q: '如何在韩漫画网找到想看的漫画？',
      a: '您可以通过顶部的分类浏览功能按类别查找，也可以使用搜索功能直接搜索漫画名称。我们还提供热门推荐和最新更新列表，方便您发现好作品。',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-secondary-500 text-sm font-medium tracking-widest mb-3">
              常见问题
            </p>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              关于韩漫画网
            </h2>
          </div>

          <div className="flex flex-col gap-3">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
              >
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                  <span className="text-foreground-800 font-medium text-sm pr-4">
                    {faq.q}
                  </span>
                  <span className="w-6 h-6 rounded-full bg-background-200 flex items-center justify-center flex-shrink-0 group-open:rotate-45 transition-transform duration-300">
                    <i className="ri-add-line text-foreground-500 text-sm"></i>
                  </span>
                </summary>
                <div className="px-5 pb-4 text-foreground-500 text-sm leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '韩漫画网 - 下拉式完整版韩漫免费阅读',
            description: '韩漫画网提供下拉式完整版韩漫免费阅读和无删减漫画免费阅读，韩漫大全每日更新。',
            url: 'http://uure9.cn/',
            isPartOf: {
              '@type': 'WebSite',
              name: '韩漫画网',
              url: 'http://uure9.cn/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'http://uure9.cn/',
                },
              ],
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: '韩漫画网的漫画可以免费阅读吗？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '是的，韩漫画网提供下拉式完整版韩漫免费阅读服务，所有漫画内容均可免费在线阅读，无需付费。',
                },
              },
              {
                '@type': 'Question',
                name: '韩漫画网的漫画是完整版还是删减版？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '韩漫画网坚持提供无删减漫画免费阅读，所有内容均为完整版，确保您获得最原汁原味的阅读体验。',
                },
              },
              {
                '@type': 'Question',
                name: '韩漫画网的漫画更新频率如何？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '韩漫大全每日更新，我们拥有专业的更新团队，确保热门连载漫画保持同步更新。',
                },
              },
              {
                '@type': 'Question',
                name: '韩漫画网支持哪些设备阅读？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '韩漫画网采用下拉式阅读模式，完美适配电脑、平板和手机等各种设备。',
                },
              },
              {
                '@type': 'Question',
                name: '如何在韩漫画网找到想看的漫画？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '您可以通过分类浏览、搜索功能、热门推荐和最新更新列表来发现漫画。',
                },
              },
            ],
          }),
        }}
      />
      <HeroSection />
      <FeaturedGrid />
      <LatestUpdates />
      <CTASection />
      <CategoryQuickLinks />
      <FAQSection />
    </>
  );
}